import { DEFAULT_MAP_ID as REGISTRY_DEFAULT_MAP_ID } from '../../map/map-registry';
import { isMobile } from '../../core/device';

// ── Game mode definitions (must match server/internal/game/gamemode.go) ──

export type GameModeId = 'competitive' | 'wingman' | 'deathmatch' | 'practice' | 'casual' | 'arms_race';

export interface GameModeInfo {
  id: GameModeId;
  label: string;
  description: string;
}

export const GAME_MODES: GameModeInfo[] = [
  { id: 'competitive', label: 'Competitive', description: '5v5 · MR12 · Bomb' },
  { id: 'casual', label: 'Casual', description: '10v10 · MR15 · Bomb' },
  { id: 'wingman', label: 'Wingman', description: '2v2 · MR8 · Bomb' },
  { id: 'deathmatch', label: 'Deathmatch', description: 'FFA · No rounds' },
  { id: 'arms_race', label: 'Arms Race', description: 'FFA · Weapon progression' },
  { id: 'practice', label: 'Practice', description: 'Solo · Free roam' },
];

export const DEFAULT_GAME_MODE: GameModeId = 'competitive';
export const DEFAULT_MAP_ID = REGISTRY_DEFAULT_MAP_ID;

// Number of floating CSS particles in the menu background
const PARTICLE_COUNT = isMobile() ? 8 : 25;
const LOADING_TIPS = [
  'Check corners before committing to a site take.',
  'Trade your teammate within two seconds of contact.',
  'Save utility for the retake when the bomb goes down early.',
  'Tap fire at long range to keep recoil predictable.',
  'Walk before peeking if the enemy can hear the rotate.',
  'Use the minimap before rotating off a bombsite.',
];

// BUG-UI-10 + BUG-UI-9: CSS injected at construct time.
//  - `.menu-nav { overflow-y: auto }` so 11 nav items remain reachable on
//    short viewports (sidebar markup lives in index.html, so we patch
//    behaviourally from here rather than touching the HTML shell).
//  - `:active` and `:focus-visible` mirrors of the existing `:hover` rules
//    so touch/keyboard users get the same visual feedback as mouse hover.
const MAIN_MENU_STYLE_ID = 'main-menu-touch-overflow-fallbacks';
const MAIN_MENU_STYLE_CSS = `
.menu-nav {
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(233, 69, 96, 0.4) transparent;
}
.menu-nav::-webkit-scrollbar { width: 6px; }
.menu-nav::-webkit-scrollbar-track { background: transparent; }
.menu-nav::-webkit-scrollbar-thumb {
  background: rgba(233, 69, 96, 0.3);
  border-radius: 3px;
}
.menu-nav::-webkit-scrollbar-thumb:hover { background: rgba(233, 69, 96, 0.5); }
.nav-item:active,
.nav-item:focus-visible {
  color: #e0d8a0;
  background: rgba(90, 106, 58, 0.15);
  border-left-color: rgba(90, 106, 58, 0.5);
  outline: none;
}
.nav-item:active .nav-icon,
.nav-item:focus-visible .nav-icon { opacity: 1; }
.menu-settings-btn:active,
.menu-settings-btn:focus-visible {
  color: #e0d8a0;
  outline: none;
}
`;

function injectMainMenuStyles(): void {
  // Guard for headless test environments where `document.head` is absent.
  const doc = typeof document !== 'undefined' ? document : null;
  if (!doc || typeof doc.createElement !== 'function') return;
  const head = (doc as Document).head ?? null;
  if (!head || typeof head.appendChild !== 'function') return;
  const getById = (doc as Document).getElementById?.bind(doc);
  if (getById && getById(MAIN_MENU_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = MAIN_MENU_STYLE_ID;
  style.textContent = MAIN_MENU_STYLE_CSS;
  head.appendChild(style);
}

// ── MainMenu class ──

export class MainMenu {
  private el: HTMLElement;
  private playBtn: HTMLElement;
  private browseBtn: HTMLElement;
  private marketBtn: HTMLElement;
  private casesBtn: HTMLElement | null;
  private replaysBtn: HTMLElement;
  private clansBtn: HTMLElement;
  private tournamentsBtn: HTMLElement;
  private overwatchBtn: HTMLElement | null;
  private statsBtn: HTMLElement | null;
  private leaderboardBtn: HTMLElement | null;
  private socialBtn: HTMLElement | null;
  private modeSelect: HTMLSelectElement;
  private navItems: HTMLElement[];
  private onPlay: (() => void) | null = null;
  private onBrowse: (() => void) | null = null;
  private onMarket: (() => void) | null = null;
  private onCases: (() => void) | null = null;
  private onReplays: (() => void) | null = null;
  private onClans: (() => void) | null = null;
  private onTournaments: (() => void) | null = null;
  private onOverwatch: (() => void) | null = null;
  private onStats: (() => void) | null = null;
  private onLeaderboard: (() => void) | null = null;
  private onSocial: (() => void) | null = null;
  private visible: boolean = true;
  private boundPlayHandler: (() => void) | null = null;
  private boundBrowseHandler: (() => void) | null = null;
  private boundMarketHandler: (() => void) | null = null;
  private boundCasesHandler: (() => void) | null = null;
  private boundReplaysHandler: (() => void) | null = null;
  private boundClansHandler: (() => void) | null = null;
  private boundTournamentsHandler: (() => void) | null = null;
  private boundOverwatchHandler: (() => void) | null = null;
  private boundStatsHandler: (() => void) | null = null;
  private boundLeaderboardHandler: (() => void) | null = null;
  private boundSocialHandler: (() => void) | null = null;
  private boundNavHandlers: Array<{ el: HTMLElement; handler: () => void }> = [];
  private tipEl: HTMLElement | null = null;
  private tipTimer: ReturnType<typeof setInterval> | null = null;
  private tipFadeTimer: ReturnType<typeof setTimeout> | null = null;
  private tipIndex = 0;
  private createdTipEl = false;

  constructor() {
    // Inject overflow + touch/keyboard-feedback CSS (BUG-UI-10 / BUG-UI-9).
    // Idempotent — guarded against headless environments + double-mount.
    injectMainMenuStyles();

    this.el = document.getElementById('main-menu')!;
    this.playBtn = document.getElementById('play-btn')!;
    this.browseBtn = document.getElementById('browse-btn')!;
    this.marketBtn = document.getElementById('market-btn')!;
    this.casesBtn = document.getElementById('cases-btn');
    this.replaysBtn = document.getElementById('replays-btn')!;
    this.clansBtn = document.getElementById('clans-btn')!;
    this.tournamentsBtn = document.getElementById('tournaments-btn')!;
    this.overwatchBtn = document.getElementById('overwatch-btn');
    this.statsBtn = document.getElementById('stats-btn');
    this.leaderboardBtn = document.getElementById('leaderboard-btn');
    this.socialBtn = document.getElementById('social-btn');

    // Mode selector — populate options
    this.modeSelect = document.getElementById('mode-select') as HTMLSelectElement;
    if (this.modeSelect) {
      for (const mode of GAME_MODES) {
        const opt = document.createElement('option');
        opt.value = mode.id;
        opt.textContent = `${mode.label}  —  ${mode.description}`;
        this.modeSelect.appendChild(opt);
      }
      this.modeSelect.value = DEFAULT_GAME_MODE;
    }

    // Nav items — find all buttons with data-panel attribute
    this.navItems = Array.from(
      this.el.querySelectorAll('.nav-item[data-panel]') as NodeListOf<HTMLElement>,
    );

    // Wire up sidebar navigation — nav items handle visual highlighting only.
    // External callbacks (market, clans, etc.) are fired via setOnXxx direct handlers.
    for (const item of this.navItems) {
      const handler = () => {
        this.setActiveNav(item);
      };
      item.addEventListener('click', handler);
      this.boundNavHandlers.push({ el: item, handler });
    }

    // Spawn CSS floating particles
    this.spawnParticles();
    this.setupLoadingTips();
  }

  /** Spawn floating CSS particles in the menu background. */
  private spawnParticles(): void {
    const container = document.getElementById('menu-particles');
    if (!container) return;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const p = document.createElement('div');
      p.className = 'menu-particle';
      p.style.left = `${Math.random() * 100}%`;
      p.style.animationDuration = `${8 + Math.random() * 12}s`;
      p.style.animationDelay = `${Math.random() * 10}s`;
      p.style.width = `${1 + Math.random() * 3}px`;
      p.style.height = p.style.width;
      container.appendChild(p);
    }
  }

  private setupLoadingTips(): void {
    const footer = this.el.querySelector?.('.menu-sidebar-footer') as HTMLElement | null | undefined;
    const existing = document.getElementById('menu-loading-tip');
    if (existing) {
      this.tipEl = existing;
    } else if (footer) {
      this.tipEl = document.createElement('span');
      this.tipEl.id = 'menu-loading-tip';
      Object.assign(this.tipEl.style, {
        flex: '1',
        minWidth: '0',
        marginLeft: '16px',
        color: 'rgba(200, 192, 144, 0.45)',
        fontSize: '10px',
        letterSpacing: '1px',
        textAlign: 'right',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        opacity: '1',
        transition: 'opacity 0.3s ease',
      });
      footer.appendChild(this.tipEl);
      this.createdTipEl = true;
    }
    if (!this.tipEl) return;

    // Ensure transition is set even when reusing a pre-existing element
    // (e.g. when index.html ships #menu-loading-tip directly).
    this.tipEl.style.transition = 'opacity 0.3s ease';
    this.tipEl.style.opacity = '1';

    // First tip shows instantly (no initial fade-out from blank).
    this.tipEl.textContent = LOADING_TIPS[this.tipIndex];
    this.tipIndex = (this.tipIndex + 1) % LOADING_TIPS.length;

    const rotateTip = () => {
      if (!this.tipEl) return;
      const target = this.tipEl;
      // Fade out, swap text, fade back in.
      target.style.opacity = '0';
      this.tipFadeTimer = setTimeout(() => {
        this.tipFadeTimer = null;
        if (!this.tipEl) return;
        this.tipEl.textContent = LOADING_TIPS[this.tipIndex];
        this.tipIndex = (this.tipIndex + 1) % LOADING_TIPS.length;
        this.tipEl.style.opacity = '1';
      }, 300);
    };
    this.tipTimer = setInterval(rotateTip, 6000);
  }

  /** Set the active nav item (highlight + deactivate others). */
  private setActiveNav(activeItem: HTMLElement): void {
    for (const item of this.navItems) {
      item.classList.remove('active');
    }
    activeItem.classList.add('active');
  }

  /** Get the currently selected game mode. */
  getSelectedMode(): GameModeId {
    return (this.modeSelect?.value ?? DEFAULT_GAME_MODE) as GameModeId;
  }

  /** Single-map build: always returns DEFAULT_MAP_ID. */
  getSelectedMapId(): string {
    return DEFAULT_MAP_ID;
  }

  show(): void {
    this.el.style.display = 'flex';
    this.visible = true;
    // Reset nav to "play" on re-show
    const playNav = this.navItems.find(n => n.getAttribute('data-panel') === 'play');
    if (playNav) this.setActiveNav(playNav);
  }

  hide(): void {
    this.el.style.display = 'none';
    this.visible = false;
  }

  setOnPlay(callback: () => void): void {
    this.onPlay = callback;
    if (this.boundPlayHandler) this.playBtn.removeEventListener('click', this.boundPlayHandler);
    this.boundPlayHandler = () => { if (this.onPlay) this.onPlay(); };
    this.playBtn.addEventListener('click', this.boundPlayHandler);
  }

  setOnBrowse(callback: () => void): void {
    this.onBrowse = callback;
    if (this.boundBrowseHandler) this.browseBtn.removeEventListener('click', this.boundBrowseHandler);
    this.boundBrowseHandler = () => { if (this.onBrowse) this.onBrowse(); };
    this.browseBtn.addEventListener('click', this.boundBrowseHandler);
  }

  setOnMarket(callback: () => void): void {
    this.onMarket = callback;
    if (this.boundMarketHandler) this.marketBtn.removeEventListener('click', this.boundMarketHandler);
    this.boundMarketHandler = () => { if (this.onMarket) this.onMarket(); };
    this.marketBtn.addEventListener('click', this.boundMarketHandler);
  }

  setOnReplays(callback: () => void): void {
    this.onReplays = callback;
    if (this.boundReplaysHandler) this.replaysBtn.removeEventListener('click', this.boundReplaysHandler);
    this.boundReplaysHandler = () => { if (this.onReplays) this.onReplays(); };
    this.replaysBtn.addEventListener('click', this.boundReplaysHandler);
  }

  /** F-CM-009: open the case-opener panel. */
  setOnCases(callback: () => void): void {
    this.onCases = callback;
    if (!this.casesBtn) return;
    if (this.boundCasesHandler) this.casesBtn.removeEventListener('click', this.boundCasesHandler);
    this.boundCasesHandler = () => { if (this.onCases) this.onCases(); };
    this.casesBtn.addEventListener('click', this.boundCasesHandler);
  }

  setOnClans(callback: () => void): void {
    this.onClans = callback;
    if (this.boundClansHandler) this.clansBtn.removeEventListener('click', this.boundClansHandler);
    this.boundClansHandler = () => { if (this.onClans) this.onClans(); };
    this.clansBtn.addEventListener('click', this.boundClansHandler);
  }

  setOnTournaments(callback: () => void): void {
    this.onTournaments = callback;
    if (this.boundTournamentsHandler) this.tournamentsBtn.removeEventListener('click', this.boundTournamentsHandler);
    this.boundTournamentsHandler = () => { if (this.onTournaments) this.onTournaments(); };
    this.tournamentsBtn.addEventListener('click', this.boundTournamentsHandler);
  }

  setOnOverwatch(callback: () => void): void {
    this.onOverwatch = callback;
    if (!this.overwatchBtn) return;
    if (this.boundOverwatchHandler) this.overwatchBtn.removeEventListener('click', this.boundOverwatchHandler);
    this.boundOverwatchHandler = () => { if (this.onOverwatch) this.onOverwatch(); };
    this.overwatchBtn.addEventListener('click', this.boundOverwatchHandler);
  }

  setOnStats(callback: () => void): void {
    this.onStats = callback;
    if (!this.statsBtn) return;
    if (this.boundStatsHandler) this.statsBtn.removeEventListener('click', this.boundStatsHandler);
    this.boundStatsHandler = () => { if (this.onStats) this.onStats(); };
    this.statsBtn.addEventListener('click', this.boundStatsHandler);
  }

  setOnLeaderboard(callback: () => void): void {
    this.onLeaderboard = callback;
    if (!this.leaderboardBtn) return;
    if (this.boundLeaderboardHandler) this.leaderboardBtn.removeEventListener('click', this.boundLeaderboardHandler);
    this.boundLeaderboardHandler = () => { if (this.onLeaderboard) this.onLeaderboard(); };
    this.leaderboardBtn.addEventListener('click', this.boundLeaderboardHandler);
  }

  // W-C-003: open SocialPanel (friend list) — gives /api/friends* server
  // endpoints at least one client UI caller, same wiring shape as
  // setOnLeaderboard / U-H-002.
  setOnSocial(callback: () => void): void {
    this.onSocial = callback;
    if (!this.socialBtn) return;
    if (this.boundSocialHandler) this.socialBtn.removeEventListener('click', this.boundSocialHandler);
    this.boundSocialHandler = () => { if (this.onSocial) this.onSocial(); };
    this.socialBtn.addEventListener('click', this.boundSocialHandler);
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    if (this.tipTimer) {
      clearInterval(this.tipTimer);
      this.tipTimer = null;
    }
    if (this.tipFadeTimer) {
      clearTimeout(this.tipFadeTimer);
      this.tipFadeTimer = null;
    }
    if (this.createdTipEl) {
      this.tipEl?.remove();
      this.createdTipEl = false;
    }
    this.tipEl = null;
    if (this.boundPlayHandler) {
      this.playBtn.removeEventListener('click', this.boundPlayHandler);
      this.boundPlayHandler = null;
    }
    if (this.boundBrowseHandler) {
      this.browseBtn.removeEventListener('click', this.boundBrowseHandler);
      this.boundBrowseHandler = null;
    }
    if (this.boundMarketHandler) {
      this.marketBtn.removeEventListener('click', this.boundMarketHandler);
      this.boundMarketHandler = null;
    }
    if (this.boundCasesHandler && this.casesBtn) {
      this.casesBtn.removeEventListener('click', this.boundCasesHandler);
      this.boundCasesHandler = null;
    }
    if (this.boundReplaysHandler) {
      this.replaysBtn.removeEventListener('click', this.boundReplaysHandler);
      this.boundReplaysHandler = null;
    }
    if (this.boundClansHandler) {
      this.clansBtn.removeEventListener('click', this.boundClansHandler);
      this.boundClansHandler = null;
    }
    if (this.boundTournamentsHandler) {
      this.tournamentsBtn.removeEventListener('click', this.boundTournamentsHandler);
      this.boundTournamentsHandler = null;
    }
    if (this.boundOverwatchHandler && this.overwatchBtn) {
      this.overwatchBtn.removeEventListener('click', this.boundOverwatchHandler);
      this.boundOverwatchHandler = null;
    }
    if (this.boundStatsHandler && this.statsBtn) {
      this.statsBtn.removeEventListener('click', this.boundStatsHandler);
      this.boundStatsHandler = null;
    }
    if (this.boundLeaderboardHandler && this.leaderboardBtn) {
      this.leaderboardBtn.removeEventListener('click', this.boundLeaderboardHandler);
      this.boundLeaderboardHandler = null;
    }
    if (this.boundSocialHandler && this.socialBtn) {
      this.socialBtn.removeEventListener('click', this.boundSocialHandler);
      this.boundSocialHandler = null;
    }
    // Clean up nav handlers
    for (const { el, handler } of this.boundNavHandlers) {
      el.removeEventListener('click', handler);
    }
    this.boundNavHandlers = [];
    this.onPlay = null;
    this.onBrowse = null;
    this.onMarket = null;
    this.onCases = null;
    this.onReplays = null;
    this.onClans = null;
    this.onTournaments = null;
    this.onOverwatch = null;
    this.onStats = null;
    this.onLeaderboard = null;
    this.onSocial = null;
  }
}
